import { cloneDeep, get, isEmpty } from 'lodash'
import { useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  AzureOfferPlan,
  Installments,
  RecurrentPriceModeType,
} from '../../../../../modules/privateOffer/reducer'
import { Product } from '../../../../../modules/productsListing/reducer'
import { isNonEmptyNumber } from '@labrav/common'
import { ContractDetail } from '../ContractDetail/ContractDetail'
import { useStyles } from '../PlanRow/PlanRow.styles'
import { Divider } from '@mui/material'
import { PaymentType } from '../PaymentType/PaymentType'
import { InstallmentsSchedule } from '../InstallmentsSchedule/InstallmentsSchedule'
import {
  calculateDiscountPercentage,
  calculateMarkupPercentage,
} from '@labrav/common'
import { MeteringPlans } from '../MeteringPlans/MeteringPlans'
import { MinMax } from '../MinMax/MinMax'
import { markupValidationMsg } from '../../../constants'
import clsx from 'clsx'
import { getPaymentOptionDisplayValue } from '../../../../MicrosoftViewPricing/getPaymentOptionDisplayValue'

export const PlanRowComponent = ({
  index,
  plan,
  product,
}: {
  index: number
  plan: AzureOfferPlan
  product: Product
}) => {
  const mppoDuration = getMppoDuration({ plan })
  const { classes } = useStyles()
  const { values, setFieldValue, errors, touched } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const markupPercentage = plan?.markupPercentage
  const paymentTermItem = useMemo(() => {
    return {
      ...cloneDeep(plan.pricing.recurrentPrice.prices[0].paymentOption),
    }
  }, [plan])

  const billingTerm = plan?.pricing.recurrentPrice?.prices[0]?.billingTerm
  const paymentOption = plan?.pricing?.recurrentPrice?.prices[0]?.paymentOption

  const paymentOptionValue = getPaymentOptionDisplayValue(
    billingTerm,
    paymentOption
  )

  const selectedPaymentOptionValue = useMemo(() => {
    if (
      plan.pricing.recurrentPrice.prices[0]?.installments &&
      plan.pricing.recurrentPrice.prices[0]?.installments?.schedule?.length
    ) {
      return 'flexiblePayment'
    }

    if (isNonEmptyNumber(paymentTermItem?.value)) {
      return paymentOptionValue
    }

    return ''
  }, [paymentTermItem, paymentOptionValue, plan, values])

  const handleChangeInstallmentsAmount = async (
    installments: Installments[]
  ) => {
    const rawTotal = installments.reduce((sum, installment) => {
      const amount = installment.pricePerPaymentInUsd || 0
      return sum + (typeof amount === 'number' ? amount : 0)
    }, 0)

    const total = parseFloat(rawTotal.toFixed(5))

    const originalPricingPrices =
      plan?.originalPricing?.recurrentPrice?.prices?.filter(
        item =>
          item?.billingTerm?.type ===
            plan.pricing.recurrentPrice.prices[0].billingTerm.type &&
          item?.billingTerm?.value ===
            plan.pricing.recurrentPrice.prices[0].billingTerm.value
      ) || []
    if (originalPricingPrices.length > 0) {
      const originalPrice = originalPricingPrices[0].pricePerPaymentInUsd || 0

      if (isNonEmptyNumber(originalPrice) && originalPrice > 0) {
        const calculatedDiscountPercentage = calculateDiscountPercentage(
          originalPrice,
          total
        )

        await setFieldValue(
          `draftOffer.plans.${index}.discountPercentage`,
          calculatedDiscountPercentage
        )
      }
    }
    if (isNonEmptyNumber(plan?.buyerPrice)) {
      const calculatedMarkupPercentage = calculateMarkupPercentage(
        total,
        plan?.buyerPrice
      )

      await setFieldValue(
        `draftOffer.plans.${index}.markupPercentage`,
        calculatedMarkupPercentage
      )
    }

    await setFieldValue(
      `draftOffer.plans.${index}.pricing.recurrentPrice.prices[0].installments.tcv`,
      total
    )
  }
  const shouldShowUserMinMax =
    plan?.pricing?.recurrentPrice?.recurrentPriceMode ===
    RecurrentPriceModeType.PERUSER
  const shouldShowMetering =
    plan?.pricing?.recurrentPrice?.recurrentPriceMode ===
      RecurrentPriceModeType.FLATRATE &&
    plan?.pricing?.customMeters &&
    Object.keys(plan.pricing?.customMeters?.meters || {}).length > 0

  const buyerError = !!(
    get(touched, `draftOffer.plans.${[index]}.buyerPrice`, false) &&
    get(errors, `draftOffer.plans.${[index]}.buyerPrice`, false)
  )
  const isErrForMarkup =
    buyerError &&
    get(errors, `draftOffer.plans.${[index]}.buyerPrice`, '') ===
      markupValidationMsg
  return (
    <>
      <div className={classes.planSectionWrapper}>
        <div className={classes.planSectionHeading}>Contract details</div>
        <ContractDetail
          index={index}
          plan={plan}
          product={product}
          key={plan.labraPlanId}
          selectedPaymentOptionValue={selectedPaymentOptionValue}
        />
      </div>
      <div className={clsx(classes.planSectionHeading)}>
        Calculated markup percentage:{' '}
        <span className={isErrForMarkup ? classes.errorMessage : ''}>
          {markupPercentage}%
        </span>
      </div>
      <Divider />
      <div className={classes.planSectionWrapper}>
        <div className={classes.planSectionHeading}>Pricing model</div>
        <PaymentType
          plan={plan}
          index={index}
          durationInMonths={mppoDuration}
          selectedPaymentOptionValue={selectedPaymentOptionValue}
        />
      </div>
      {!isEmpty(plan.pricing.recurrentPrice.prices[0]?.installments) &&
        mppoDuration > 1 && (
          <>
            <Divider />
            <InstallmentsSchedule
              plan={plan}
              installments={
                plan.pricing.recurrentPrice.prices[0]?.installments?.schedule
              }
              index={index}
              durationInMonths={mppoDuration}
              offerStartDate={(values?.offerStartDate as string) || ''}
              tcv={plan?.pricing.recurrentPrice.prices[0].installments?.tcv}
              handleChangeInstallmentsAmount={handleChangeInstallmentsAmount}
            />
          </>
        )}
      {(shouldShowUserMinMax || shouldShowMetering) && (
        <Divider className={classes.divider} />
      )}
      {shouldShowUserMinMax && <MinMax plan={plan} index={index} />}
      {shouldShowMetering && <MeteringPlans plan={plan} index={index} />}
    </>
  )
}

export const getMppoDuration = ({ plan }: { plan: AzureOfferPlan }) => {
  if (
    !plan ||
    !plan?.pricing ||
    !plan?.pricing?.recurrentPrice ||
    !plan?.pricing?.recurrentPrice?.prices ||
    !plan?.pricing?.recurrentPrice?.prices[0] ||
    !plan?.pricing?.recurrentPrice?.prices[0]?.billingTerm
  ) {
    return 0
  }
  const billingTermItem = {
    ...cloneDeep(plan.pricing.recurrentPrice.prices[0].billingTerm),
  }
  return getDurationValueFromBillingTerm(billingTermItem)
}

export const getDurationValueFromBillingTerm = (billingTerm: {
  type: string
  value: number
}) => {
  if (billingTerm.type === 'month') {
    return billingTerm.value
  } else if (billingTerm.type === 'year') {
    return billingTerm.value * 12
  }

  return 0
}
